import { DownOutlined } from "@ant-design/icons"
import { Checkbox, ConfigProvider, Dropdown, Menu, Space, Typography } from "antd"
import { FC, useMemo } from "react"
import { useNavigate } from "react-router"
import { useAuth } from "../../../../../../../components/auth"
import {
  useBlockDistributorsMutation,
  useBlockUsersMutation,
  useGetRegionsQuery,
  useUnblockDistributorsMutation,
  useUnblockUsersMutation,
} from "../../../../../../../graphql"
import { truncate } from "../../../../../../../utils"
import "./index.less"

import { StatusFilter } from "@/components/status-filter"
import { useBlockNotification } from "@/hooks/useBlockNotification"
import { useSingleFilterParam } from "@/hooks/useSingleFilterParam"
import type { CheckboxProps } from "antd"
import { DangerInfoIcon } from "../../../../../../../icons"
import ToolTip from "../../../../../components/ToolTip"

const Filter: FC<TeamFilterInterface> = ({ filters, setFilters, roleFilters, role, createList }) => {
  const navigate = useNavigate()
  const { region } = useAuth()
  const { data: regionsData } = useGetRegionsQuery()
  const { value: selectedDistributers, resetValue: resetDistributers } = useSingleFilterParam("distributors")
  const { value: selectedUsers, resetValue: resetUsers } = useSingleFilterParam("users")

  const [unblockDistributors] = useUnblockDistributorsMutation()
  const [blockDistributors] = useBlockDistributorsMutation()
  const [blockUsers] = useBlockUsersMutation()
  const [unblockUsers] = useUnblockUsersMutation()

  const getRegionOptions = () => {
    selectedDistributers
    const regionOptions = Array.from({ length: regionsData?.regions?.data.length || 0 }, (_, i) => ({
      label: regionsData?.regions?.data[i].attributes?.name,
      key: i + 1,
    }))
    if (!region?.id) {
      regionOptions.unshift({
        label: "All",
        key: 0,
      })
    }
    return regionOptions || []
  }

  const regionOptions = getRegionOptions()

  const statusOptions = ["All", "Active", "Blocked"].map((label, index) => ({ label, key: index + 1 }))

  const resetFilter = (type: string) => {
    switch (type) {
      case "name":
        setFilters({ ...filters, selectedName: "All" })
        break
      case "email":
        setFilters({ ...filters, selectedEmail: "All" })
        break
      case "Sales Manager":
        setFilters({ ...filters, selectedSM: "All" })
        break
      case "Distributor":
        setFilters({ ...filters, selectedDistributor: "All" })
        break
    }
  }

  const onCheckboxChange: CheckboxProps["onChange"] = e => {
    setFilters({
      ...filters,
      isBadgesOnly: e.target.checked,
    })
  }

  const isDistributorEntity = role === "Distributor Entity"

  const roleActiveFiltersAmount = roleFilters.length

  const currentRegion = regionOptions.find(item => item.label === filters.selectedRegion) ?? regionOptions[0]

  const selected = isDistributorEntity ? selectedDistributers : selectedUsers

  const config = useMemo(
    () =>
      isDistributorEntity
        ? {
            key: "distributor_ids",
            block: blockDistributors,
            unblock: unblockDistributors,
            reset: resetDistributers,
          }
        : {
            key: "user_ids",
            block: blockUsers,
            unblock: unblockUsers,
            reset: resetUsers,
          },
    [
      isDistributorEntity,
      blockDistributors,
      unblockDistributors,
      resetDistributers,
      blockUsers,
      unblockUsers,
      resetUsers,
    ]
  )

  useBlockNotification({
    selected,
    config,
    onSuccess: createList,
  })

  return (
    <div className={`filters-wrapper team-filter ${role?.toLowerCase()?.replace(" ", "-")}`}>
      <div className={`filters ${role == "Distributor" && region?.id && "justify-right"}`}>
        {!region?.id && (
          <Dropdown
            className={`region-filter ${filters.selectedRegion != "All" && "active"}`}
            trigger={["click"]}
            overlay={
              <Menu
                onClick={e => {
                  const found = regionOptions.find(item => String(item.key) === String(e.key))
                  if (found?.label) {
                    setFilters({ ...filters, selectedRegion: found.label })
                  }
                }}
                selectedKeys={[String(currentRegion?.key ?? "")]}
              >
                {regionOptions.map(item => (
                  <Menu.Item key={item.key}>{item.label}</Menu.Item>
                ))}
              </Menu>
            }
          >
            <Typography.Link>
              <Space className={"dropdown"}>
                {truncate(currentRegion?.label ?? "Select Region", 18)}
                <DownOutlined style={{ color: "black" }} />
              </Space>
            </Typography.Link>
          </Dropdown>
        )}

        {role !== "Distributor Entity" && <StatusFilter filters={filters} setFilters={setFilters} />}

        {isDistributorEntity && (
          <Dropdown
            className={`status-filter ${!(filters.selectedStatus as string[])?.includes("All") && "active"}`}
            trigger={["click"]}
            overlay={
              <Menu
                onClick={e => {
                  const found = statusOptions.find(item => String(item.key) === String(e.key))
                  if (found?.label) {
                    setFilters({ ...filters, selectedStatus: [found.label] })
                  }
                }}
                selectedKeys={statusOptions
                  .filter(item => (filters.selectedStatus as string[])?.includes(item.label))
                  .map(item => String(item.key))}
              >
                {statusOptions.map(item => (
                  <Menu.Item key={item.key}>{item.label}</Menu.Item>
                ))}
              </Menu>
            }
          >
            <Typography.Link>
              <Space className={"dropdown"}>
                {truncate((filters.selectedStatus as string[])?.[0] ?? "All", 18)}
                <DownOutlined style={{ color: "black" }} />
              </Space>
            </Typography.Link>
          </Dropdown>
        )}

        {role == "DSC" && (
          <ConfigProvider
            theme={{
              components: {
                Checkbox: {
                  colorBorder: "#000",
                  colorPrimaryHover: "#000",
                  colorPrimary: "#000",
                  borderRadius: 0,
                },
              },
            }}
          >
            <div className={"checkbox-wrapper"}>
              <Checkbox onChange={onCheckboxChange}>Badge only</Checkbox>
              <ToolTip
                title={
                  <div className={"checkbox-tool-tip"}>
                    Users with <span children={"Gold"} />, <span children={"Platinum"} /> or{" "}
                    <span children={"Diamond"} /> badges only
                  </div>
                }
                disabled={false}
                size={"fit-content"}
              >
                <DangerInfoIcon />
              </ToolTip>
            </div>
          </ConfigProvider>
        )}
      </div>

      <div className={"role-filters-wrapper"}>
        Filters ({roleActiveFiltersAmount})
        <div className={"role-filters-list"}>
          {roleFilters &&
            roleFilters.map((filter, i) => (
              <div
                className={"block"}
                key={`filter-${i}`}
                style={{ backgroundColor: filter.backgroundColor, color: filter.color }}
              >
                {truncate(filter.option, 10)}
                <div className={"remove-filter"} onClick={() => resetFilter(filter.type)}>
                  <div className={"l-line"} />
                  <div className={"r-line"} />
                </div>
              </div>
            ))}
        </div>
      </div>

      {(role == "Distributor" || role == "Distributor Entity") && (
        <div className={"tabs-wrapper"}>
          Tabs:
          <div className={"tabs"}>
            <div
              className={role == "Distributor" ? "active" : ""}
              onClick={() => navigate("/account/team/distributor/users")}
            >
              ASSIGNED USERS
            </div>
            <div
              className={role == "Distributor Entity" ? "active" : ""}
              onClick={() => navigate("/account/team/distributor/entities")}
            >
              DISTRIBUTORS
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Filter
